export const WalletLoginLockKey = 'wallet-login-lock';
export const WalletLoginTabIdKey = 'wallet-login-tab-id';

export interface WalletLoginLockRecord {
    sessionKey: string;
    ownerTabId: string;
    expiresAt: number;
}

export interface WalletLoginLockOptions {
    localStorage?: Storage;
    sessionStorage?: Storage;
    now?: number;
    ttlMs?: number;
}

export interface WalletLoginLockResult {
    acquired: boolean;
    ownerTabId?: string;
    expiresAt?: number;
}

export interface WaitForWalletLoginTokenOptions {
    timeoutMs?: number;
    pollMs?: number;
    isCurrentSession?: () => boolean;
}

const DefaultLockTtlMs = 45_000;
const DefaultTokenWaitPollMs = 250;

const getDefaultStorage = (type: 'local' | 'session'): Storage | undefined => {
    if (typeof window === 'undefined') return undefined;
    return type === 'local' ? window.localStorage : window.sessionStorage;
};

const randomTabId = (): string => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const parseLock = (value: string | null): WalletLoginLockRecord | undefined => {
    if (!value) return undefined;
    try {
        const record = JSON.parse(value) as WalletLoginLockRecord;
        if (!record.sessionKey || !record.ownerTabId || typeof record.expiresAt !== 'number') return undefined;
        return record;
    } catch {
        return undefined;
    }
};

export const getWalletLoginTabId = (storage = getDefaultStorage('session'), createId = randomTabId): string => {
    const existing = storage?.getItem(WalletLoginTabIdKey);
    if (existing) return existing;

    const tabId = createId();
    storage?.setItem(WalletLoginTabIdKey, tabId);
    return tabId;
};

export const tryAcquireWalletLoginLock = (sessionKey: string, options: WalletLoginLockOptions = {}): WalletLoginLockResult => {
    const local = options.localStorage || getDefaultStorage('local');
    const session = options.sessionStorage || getDefaultStorage('session');
    if (!local) return { acquired: true };

    const now = options.now ?? Date.now();
    const ttlMs = options.ttlMs ?? DefaultLockTtlMs;
    const tabId = getWalletLoginTabId(session);
    const existing = parseLock(local.getItem(WalletLoginLockKey));
    const isBlocked = existing && existing.sessionKey === sessionKey && existing.ownerTabId !== tabId && existing.expiresAt > now;

    if (isBlocked) {
        return { acquired: false, ownerTabId: existing.ownerTabId, expiresAt: existing.expiresAt };
    }

    local.setItem(
        WalletLoginLockKey,
        JSON.stringify({
            sessionKey,
            ownerTabId: tabId,
            expiresAt: now + ttlMs,
        } satisfies WalletLoginLockRecord)
    );
    return { acquired: true, ownerTabId: tabId, expiresAt: now + ttlMs };
};

export const clearWalletLoginLock = (sessionKey: string, options: WalletLoginLockOptions = {}): void => {
    const local = options.localStorage || getDefaultStorage('local');
    const session = options.sessionStorage || getDefaultStorage('session');
    if (!local) return;

    const tabId = getWalletLoginTabId(session);
    const existing = parseLock(local.getItem(WalletLoginLockKey));
    if (!existing || existing.sessionKey !== sessionKey || existing.ownerTabId !== tabId) return;

    local.removeItem(WalletLoginLockKey);
};

export const waitForWalletLoginToken = async <T>(readToken: () => T | undefined, options: WaitForWalletLoginTokenOptions = {}): Promise<T | undefined> => {
    const timeoutMs = options.timeoutMs ?? DefaultLockTtlMs;
    const pollMs = options.pollMs ?? DefaultTokenWaitPollMs;
    const startedAt = Date.now();

    return new Promise((resolve) => {
        const check = () => {
            const token = readToken();
            if (token) {
                resolve(token);
                return;
            }
            if (options.isCurrentSession && !options.isCurrentSession()) {
                resolve(undefined);
                return;
            }
            if (Date.now() - startedAt >= timeoutMs) {
                resolve(undefined);
                return;
            }
            setTimeout(check, pollMs);
        };

        check();
    });
};
